import React, { useCallback, useMemo, useState } from 'react';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import NotificationItem from 'components/common/NotificationItem';
import classNames from 'classnames';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import {
  getNotifications,
  GetNotificationsParams,
  markAllNotificationsAsRead,
  markAllNotificationsAsArchived,
  Notification
} from 'services/api/notification';
import { Button, Spinner, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { notificationsBreadcrumbItems } from 'data/notifications';
import { useNotificationSocket } from 'hooks/useNotificationSocket';
import { useAccessToken } from 'providers/AccessTokenProvider';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const NOTIFICATIONS_PER_PAGE = 20;

const formatDateToUSA = (date?: string) => {
  if (!date) return '';

  const [year, month, day] = date.split('-');

  if (!year || !month || !day) {
    return date;
  }

  return `${month}/${day}/${year}`;
};

const parseDateString = (dateString?: string) => {
  if (!dateString) return null;

  const parts = dateString.split('-');
  if (parts.length !== 3) return null;

  const [year, month, day] = parts;
  const yearNum = Number(year);
  const monthNum = Number(month);
  const dayNum = Number(day);

  if (!yearNum || !monthNum || !dayNum) return null;

  return new Date(yearNum, monthNum - 1, dayNum);
};

const Notifications = () => {
  const queryClient = useQueryClient();
  const { authUser } = useAccessToken();
  const [isMarkingAllRead, setIsMarkingAllRead] = useState<boolean>(false);
  const [isArchivingAll, setIsArchivingAll] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    read: null | boolean;
    type?: string;
    archived?: boolean | null;
    startDate?: string;
    endDate?: string;
  }>({
    read: null,
    type: '',
    archived: null,
    startDate: '',
    endDate: ''
  });

  const startDateValue = useMemo(
    () => parseDateString(filters.startDate),
    [filters.startDate]
  );
  const endDateValue = useMemo(
    () => parseDateString(filters.endDate),
    [filters.endDate]
  );

  const {
    data: notificationsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch
  } = useInfiniteQuery(
    ['notifications', filters],
    ({ pageParam = 1 }) => {
      const params: GetNotificationsParams = {
        page: pageParam,
        limit: NOTIFICATIONS_PER_PAGE
      };

      if (filters.read !== null) {
        params.read = filters.read;
      }

      if (filters.type) {
        params.type = filters.type;
      }

      if (filters.archived !== null) {
        params.archived = filters.archived;
      }

      const formattedStartDate = formatDateToUSA(filters.startDate);
      if (formattedStartDate) {
        params.startDate = formattedStartDate;
      }

      const formattedEndDate = formatDateToUSA(filters.endDate);
      if (formattedEndDate) {
        params.endDate = formattedEndDate;
      }

      return getNotifications(params);
    },
    {
      getNextPageParam: lastPage => {
        const response = lastPage.data;
        return response.page < Math.ceil(response.total / response.limit)
          ? response.page + 1
          : undefined;
      },
      staleTime: 60 * 1000
    }
  );

  const markAllReadMutation = useMutation(markAllNotificationsAsRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notificationCount']);
      toast.success('All notifications marked as read');
      setIsMarkingAllRead(false);
    },
    onError: () => {
      toast.error('Failed to mark notifications as read');
      setIsMarkingAllRead(false);
    }
  });

  const markAllArchivedMutation = useMutation(markAllNotificationsAsArchived, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notificationCount']);
      toast.success('All notifications archived');
      setIsArchivingAll(false);
    },
    onError: () => {
      toast.error('Failed to archive notifications');
      setIsArchivingAll(false);
    }
  });

  const handleMarkAllAsRead = () => {
    setIsMarkingAllRead(true);
    markAllReadMutation.mutate();
  };

  const handleMarkAllAsArchived = () => {
    setIsArchivingAll(true);
    markAllArchivedMutation.mutate();
  };

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      read: null,
      type: '',
      archived: null,
      startDate: '',
      endDate: ''
    });
    refetch();
  };

  const allNotifications =
    notificationsPages?.pages.flatMap(page => page.data.data) || [];

  // Real-time notification socket for notifications page
  useNotificationSocket({
    enabled: Boolean(authUser),
    onNewNotification: (notification: Notification) => {
      console.log('New notification received on page:', notification);

      // Add new notification to the top of the list
      queryClient.setQueryData(['notifications', filters], (old: any) => {
        if (!old) return old;

        const firstPage = old.pages[0];
        if (!firstPage) return old;

        const updatedFirstPage = {
          ...firstPage,
          data: {
            ...firstPage.data,
            data: [notification, ...firstPage.data.data],
            total: firstPage.data.total + 1
          }
        };

        return {
          ...old,
          pages: [updatedFirstPage, ...old.pages.slice(1)]
        };
      });

      // Force invalidation to ensure updates even when modals are open
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notificationCount']);

      // Show toast notification
      toast.success(`New notification: ${notification.title}`);
    },
    onNotificationCountUpdate: data => {
      console.log('Notification count updated on page:', data);
      // Update notification count cache
      queryClient.setQueryData(['notificationCount'], () => ({
        data: { count: data.count }
      }));
      // Force invalidation to ensure updates
      queryClient.invalidateQueries(['notificationCount']);
    },
    onNotificationRead: data => {
      console.log('Notification read on page:', data);

      // Update specific notification as read
      queryClient.setQueryData(['notifications', filters], (old: any) => {
        if (!old) return old;

        const updatedPages = old.pages.map((page: any) => ({
          ...page,
          data: {
            ...page.data,
            data: page.data.data.map((notification: Notification) =>
              notification.id === data.notificationId
                ? { ...notification, read: true }
                : notification
            )
          }
        }));

        return {
          ...old,
          pages: updatedPages
        };
      });

      // Force invalidation to ensure updates even when modals are open
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notificationCount']);
    },
    onAllNotificationsRead: () => {
      console.log('All notifications read on page');

      // Mark all notifications as read in the list
      queryClient.setQueryData(['notifications', filters], (old: any) => {
        if (!old) return old;

        const updatedPages = old.pages.map((page: any) => ({
          ...page,
          data: {
            ...page.data,
            data: page.data.data.map((notification: Notification) => ({
              ...notification,
              read: true
            }))
          }
        }));

        return {
          ...old,
          pages: updatedPages
        };
      });

      // Update count to 0
      queryClient.setQueryData(['notificationCount'], () => ({
        data: { count: 0 }
      }));

      // Force invalidation to ensure updates even when modals are open
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notificationCount']);
    }
  });

  return (
    <div>
      <PageBreadcrumb items={notificationsBreadcrumbItems} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Notifications</h2>
        <ButtonGroup>
          <Button
            variant="link"
            className="fs-8"
            onClick={handleMarkAllAsRead}
            disabled={isMarkingAllRead || allNotifications.length === 0}
          >
            {isMarkingAllRead ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Marking...
              </>
            ) : (
              'Mark all as read'
            )}
          </Button>
          <Button
            variant="link"
            className="fs-8"
            onClick={handleMarkAllAsArchived}
            disabled={isArchivingAll || allNotifications.length === 0}
          >
            {isArchivingAll ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Archiving...
              </>
            ) : (
              'Archive all'
            )}
          </Button>
        </ButtonGroup>
      </div>

      <Row className="mb-4 g-3">
        <Col md={2}>
          <Form.Group>
            <Form.Label>Filter by read status</Form.Label>
            <Form.Select
              value={
                filters.read === null ? '' : filters.read ? 'true' : 'false'
              }
              onChange={e => {
                const value = e.target.value;
                handleFilterChange(
                  'read',
                  value === '' ? null : value === 'true'
                );
              }}
            >
              <option value="">All</option>
              <option value="true">Read</option>
              <option value="false">Unread</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Filter by archive status</Form.Label>
            <Form.Select
              value={
                filters.archived === null
                  ? ''
                  : filters.archived
                  ? 'true'
                  : 'false'
              }
              onChange={e => {
                const value = e.target.value;
                handleFilterChange(
                  'archived',
                  value === '' ? null : value === 'true'
                );
              }}
            >
              <option value="false">Not Archived</option>
              <option value="true">Archived</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Filter by type</Form.Label>
            <Form.Select
              value={filters.type}
              onChange={e => handleFilterChange('type', e.target.value)}
            >
              <option value="">All types</option>
              <option value="feedback_submitted">Feedback Submitted</option>
              <option value="task_assigned">Task Assigned</option>
              <option value="task_completed">Task Completed</option>
              <option value="task_updated">Task Updated</option>
              <option value="task_reminder">Task Reminder</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="d-flex flex-column mt-2">
            <Form.Label className="mb-1">Start Date</Form.Label>
            <DatePicker
              selected={startDateValue}
              onChange={dateValue => {
                const selectedDate = Array.isArray(dateValue)
                  ? dateValue[0]
                  : dateValue;
                handleFilterChange(
                  'startDate',
                  selectedDate ? selectedDate.toISOString().split('T')[0] : ''
                );
              }}
              placeholderText="mm/dd/yyyy"
              className="form-control"
              wrapperClassName="w-100"
              dateFormat="MM/dd/yyyy"
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="d-flex flex-column mt-2">
            <Form.Label className="mb-1">End Date</Form.Label>
            <DatePicker
              selected={endDateValue}
              onChange={dateValue => {
                const selectedDate = Array.isArray(dateValue)
                  ? dateValue[0]
                  : dateValue;
                handleFilterChange(
                  'endDate',
                  selectedDate ? selectedDate.toISOString().split('T')[0] : ''
                );
              }}
              placeholderText="mm/dd/yyyy"
              className="form-control"
              wrapperClassName="w-100"
              dateFormat="MM/dd/yyyy"
              isClearable
            />
          </Form.Group>
        </Col>
        <Col xs={12}>
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleApplyFilters}>
              Apply Filters
            </Button>
            <Button variant="outline-secondary" onClick={handleResetFilters}>
              Reset Filters
            </Button>
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : allNotifications.length === 0 ? (
        <div className="text-center py-5">
          <p className="fs-1 mb-2">📭</p>
          <h4>No notifications found</h4>
          <p className="text-muted">
            Try adjusting your filters or check back later
          </p>
        </div>
      ) : (
        <>
          <div className="mx-n4 mx-lg-n6 border-top border-300">
            {allNotifications.map((notification, index) => (
              <NotificationItem
                notification={notification}
                type="pageItem"
                className={classNames({
                  'border-bottom': index !== allNotifications.length - 1
                })}
                key={`${notification.id}-${notification.read}-${notification.archived}`}
              />
            ))}
          </div>

          {hasNextPage && (
            <div className="text-center my-4">
              <Button
                variant="outline-primary"
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Loading more...
                  </>
                ) : (
                  'Load more notifications'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Notifications;
